<template>
    <div id="retouching">
        <HeadFoot
            title="Additional Retouching"
            v-on:back="back()"
            v-on:clicked="next()"
            button="Next"
        ></HeadFoot>

        <h5 class="text-center my-3">Enhance your photos with our retouching options!</h5>

        <div class="flex justify-center" v-if="loadingImages || loadingRetouching">
            <font-awesome-icon icon="spinner" size="2x" pulse></font-awesome-icon>
        </div>

        <div v-if="!loadingImages && images.length === 0">
            <p class="text-center text-gray-700 font-italic">No images available for retouching.</p>
        </div>

        <div
            class="flex flex-row flex-wrap justify-center"
            v-if="!loadingImages && images.length > 0"
        >
            <div class="p-3 w-64" v-for="(image, i) in images" :key="i">
                <div class="w-full">
                    <img :src="image.url" :alt="image.fnum" class="w-full" />
                </div>
                <div class="flex justify-center" v-if="loadingRetouching">
                    <font-awesome-icon icon="spinner" size="2x" pulse></font-awesome-icon>
                </div>
                <div class="text-center" v-if="retouchingProducts.length > 0">
                    <div
                        v-for="retouching in retouchingProducts.filter(p => parseInt(p.QTY_PRINTS) === 1)"
                        :key="retouching.prod_code"
                    >
                        <div class="relative flex items-start mt-2">
                            <div class="flex items-center h-5">
                                <input
                                    :id="'img_'+image.fnum"
                                    type="checkbox"
                                    class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out disabled:border-gray-50 disabled:bg-gray-400"
                                    :disabled="applyToAll"
                                    v-model="images[i].retouching"
                                />
                            </div>
                            <div class="ml-3 text-sm leading-5">
                                <label
                                    :for="'img_'+image.fnum"
                                    class="font-medium text-gray-700"
                                >{{retouching.flyername}} - ${{parseFloat(retouching.net_price).toFixed(2)}}</label>
                            </div>
                            <div class="ml-3">
                                <a
                                    role="button"
                                    data-toggle="popover"
                                    data-placement="top"
                                    data-trigger="click hover"
                                >
                                    <font-awesome-icon
                                        :icon="['far', 'circle-question']"
                                        class="text-leonard-green"
                                    ></font-awesome-icon>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="my-8"
            v-if="!anyRetouchingSelection() && images.length > 0 && retouchingProducts.filter(p => parseInt(p.QTY_PRINTS) > 1).length > 0"
        >
            <div class="w-3/4 md:w-1/3 p-4 mx-auto text-center border border-gray-100">
                <div class="text-xl font-bold">Apply to All</div>
                <div class="text-center" v-if="retouchingProducts.length > 0">
                    <div
                        v-for="retouching in retouchingProducts.filter(p => parseInt(p.QTY_PRINTS) > 1)"
                        :key="retouching.prod_code"
                    >
                        <div class="relative flex items-start mt-2">
                            <div class="flex items-center h-5">
                                <input
                                    id="apply_to_all"
                                    type="checkbox"
                                    class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                    v-model="applyToAll"
                                />
                            </div>
                            <div class="ml-3 text-sm leading-5">
                                <label
                                    for="apply_to_all"
                                    class="font-medium text-gray-700"
                                >{{retouching.flyername}} - ${{parseFloat(retouching.net_price).toFixed(2)}}</label>
                            </div>
                            <div class="ml-3">
                                <a
                                    role="button"
                                    data-toggle="popover"
                                    data-placement="top"
                                    data-trigger="click hover"
                                >
                                    <font-awesome-icon
                                        :icon="['far', 'circle-question']"
                                        class="text-leonard-green"
                                    ></font-awesome-icon>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-8" v-if="anyRetouchingSelection() || applyToAll">
            <div
                class="w-3/4 md:w-1/3 p-4 mx-auto text-center border border-gray-100"
                v-if="retouchingProducts[0].options.length > 0"
            >
                <div class="text-xl font-bold">Custom Options</div>
                <div v-for="option in retouchingProducts[0].options" :key="option.CONT_CODE">
                    <div class="relative flex items-start mt-2">
                        <div class="flex items-center h-5">
                            <input
                                :id="'option_'+option.CONT_CODE"
                                type="checkbox"
                                class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                v-model="option.selected"
                            />
                        </div>
                        <div class="ml-3 text-sm leading-5">
                            <label
                                :for="'option_'+option.CONT_CODE"
                                class="font-medium text-gray-700"
                            >{{option.NAME}}</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="flex flex-row justify-center items-center my-8"
            v-if="anyRetouchingSelection() || applyToAll"
        >
            <span class="inline-flex rounded-md shadow-sm">
                <button
                    type="button"
                    class="font-medium transition-all duration-300 border-2 rounded-md inline-flex items-center py-2 px-3 md:px-4 bg-leonard-green border-transparent text-leonard-white hover:bg-leonard-green-hover focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed"
                    @click="addToCart()"
                >
                    Add To Cart
                    <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="shopping-cart w-5 h-5 ml-2"
                        v-if="!adding"
                    >
                        <path
                            d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"
                        />
                    </svg>
                    <font-awesome-icon icon="spinner" size="2x" pulse v-if="adding"></font-awesome-icon>
                </button>
            </span>
        </div>

        <HeadFoot v-on:back="back()" v-on:clicked="next()" button="Next"></HeadFoot>
    </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons'

import HeadFoot from './partials/StepHeaderFooter'
import Cart from '../../classes/Cart'
import Photo from '../../classes/Photo'
import Retouching from '../../classes/Retouching'

export default {
    data() {
        return {
            Retouching: new Retouching(),
            Cart: new Cart(),
            Photo: new Photo(),
            con_no: localStorage.getItem('con_no'),
            school: localStorage.getItem('school'),
            access_code: localStorage.getItem('access_code'),
            images: [],
            retouchingProducts: [],
            applyToAll: false,
            adding: false,
            loadingImages: false,
            loadingRetouching: false,
        }
    },

    components: {
        HeadFoot,
    },

    created() {
        library.add(faCircleQuestion, faSpinner)
    },

    mounted() {
        window.events.$emit('finishedLoading')

        this.loadCartImages()
        this.getRetouchingOptions()
    },

    updated() {
        this.initializePopover()
    },

    methods: {
        initializePopover() {
            $('[data-toggle="popover"]').popover({
                html: true,
                content: `<ul class="list-disc list-outside m-0 p-0 px-3">
                              <li>Remove Blemishes, facial lines, dark under-eye circles</li>
                              <li>Remove stray hairs</li>
                              <li>Remove tan lines</li>
                              <li>Whiten teeth & eyes</li>
                          </ul>`,
            })
        },

        getRetouchingOptions() {
            let self = this
            let data = {
                con_no: localStorage.getItem('con_no'),
            }

            this.loadingRetouching = true
            this.Retouching.getRetouching(data)
                .then(function (response) {
                    self.retouchingProducts = response.data
                })
                .finally(function () {
                    self.loadingRetouching = false
                })
        },

        loadCartImages() {
            let self = this
            let user_id = this.$auth.user().details.id

            this.loadingImages = true
            this.Cart.getUsed(user_id)
                .then(function (response) {
                    if (!response.success) {
                        feedback('Error getting cart images', 'danger')
                        return
                    }

                    return response.data.used
                })
                .then(function (used) {
                    for (let i = 0; i < used.length; i++) {
                        let fnum = used[i]
                        self.getImageUrl(fnum).then(function (resp) {
                            self.images.push({
                                fnum: fnum,
                                url: resp.data,
                                retouching: false,
                            })
                        })
                    }

                    self.loadingImages = false
                })
        },

        async getImageUrl(fNum) {
            let film = fNum.substr(1, 6)
            let frame = fNum.substr(8, 3)
            let data = {
                con_no: this.con_no,
            }

            return this.Photo.getImage(film, frame, data)
        },

        anyRetouchingSelection() {
            return this.images.filter(img => img.retouching).length > 0
        },


        async addToCart() {
            let self = this
            let selectedPoses = this.applyToAll
                ? this.images
                : this.images.filter(img => img.retouching)
            let options = this.retouchingProducts[0].options.filter(
                option => option.selected
            )

            if (this.applyToAll) {
                // run once
                let addon = this.retouchingProducts.filter(
                    p => parseInt(p.QTY_PRINTS) > 1
                )[0]
                let contentsArray = []

                options.forEach(item =>
                    contentsArray.push({
                        name: item.NAME,
                        cont_code: item.CONT_CODE,
                        poses: [],
                        download: 0,
                        rush: 0,
                    })
                )

                let data = {
                    user_id: this.$auth.user().details.id,
                    name: addon.flyername,
                    con_no: this.con_no,
                    school: this.school,
                    accesscode: this.access_code,
                    price: parseFloat(addon.net_price).toFixed(2),
                    tax_amt: parseFloat(addon.tax_amt).toFixed(2),
                    package_number: addon.pkg_no,
                    contents: contentsArray,
                    is_rush: false,
                    is_download: false,
                }

                self.adding = true
                await this.Cart.addItem(data, data.user_id)
                    .then(function (response) {
                        self.adding = false
                        if (!response.success) return

                        window.events.$emit('addToCart')
                        // Show modal with option to continue or select another package
                        feedback('Retouching successfully added to cart')
                        self.next()
                    })
                    .catch(function (error) {
                        console.error(error);
                    })
            } else {
                // loop over all selected poses
                for (let i = 0; i < selectedPoses.length; i++) {
                    const pose = selectedPoses[i]
                    let addon = this.retouchingProducts.filter(
                        p => parseInt(p.QTY_PRINTS) === 1
                    )[0]
                    let contentsArray = [{
                        name: addon.flyername,
                        cont_code: addon.CONT_CODE,
                        poses: [pose.fnum],
                        download: 0,
                        rush: 0,
                    }];

                    options.forEach(item =>
                        contentsArray.push({
                            name: item.NAME,
                            cont_code: item.CONT_CODE,
                            poses: [pose.fnum],
                            download: 0,
                            rush: 0,
                        })
                    )

                    let data = {
                        user_id: this.$auth.user().details.id,
                        name: addon.flyername,
                        con_no: this.con_no,
                        school: this.school,
                        accesscode: this.access_code,
                        price: parseFloat(addon.net_price).toFixed(2),
                        tax_amt: parseFloat(addon.tax_amt).toFixed(2),
                        package_number: addon.pkg_no,
                        contents: contentsArray,
                        is_rush: false,
                        is_download: false,
                    }

                    self.adding = true
                    await this.addItemAjax(self, data);
                }
                feedback('Retouching successfully added to cart')
                this.next();
            }
        },

        async addItemAjax(self, data){
            await this.Cart.addItem(data, data.user_id)
                .then(function (response) {
                    self.adding = false
                    if (!response.success) return
                    window.events.$emit('addToCart')
                    // Show modal with option to continue or select another package
                })
                .catch(function (error) {
                    console.error(error);
                })
        },

        back() {
            $('[data-toggle="popover"]').popover('dispose')
            this.$emit('goToStep', 'addons')
        },

        next() {
            $('[data-toggle="popover"]').popover('dispose')
            this.$emit('nextStep')
        },
    },
}
</script>
